/* eslint-disable max-depth */
import { useEffect, createContext, useState } from 'react';
import * as Sentry from '@sentry/browser';
import DOMPurify from 'dompurify'; // use DOMPurify to clean up HTML
import useAntStaticComponent from 'common/hooks/useAntStaticComponent';
import MyIcon from '@/components/MyIcon';
import cx from './index.module.less';

export interface EventSourceContextValue {
  onlineNotificationInfo: OnlineNotificationData;
}

enum DynamicNotificationType {
  OnlineNotification = 'WEB_NOTIFICATION', // 上线通知
}

function _createMarkup(htmlString: string) {
  // 清洁 HTML 字符串，移除所有不安全的标签和属性
  const cleanHTML = DOMPurify.sanitize(htmlString, { FORBID_TAGS: ['script'] });

  return { __html: cleanHTML };
}

function _safeHTML({ htmlString }) {
  try {
    // 如果 HTML 包含恶意代码，则 createMarkup 会抛出错误
    const markup = _createMarkup(htmlString);

    // 使用 dangerouslySetInnerHTML 安全地渲染清洁后的 HTML
    return (
      <div className={cx('global-header')}>
        {/* <MyIcon icon="alert1-circle" className={cx('tip-icon')} size={16} /> */}
        <div dangerouslySetInnerHTML={markup} />
      </div>
    );
  } catch (error: any) {
    console.error(`HTML contains unsafe script tags: ${error.message}`);
    // 在这里处理错误，例如展示一个错误消息
    Sentry.captureEvent(new Error(`HTML contains unsafe script tags: ${error.message}`));
    return '';
  }
}

interface OnlineNotificationData {
  isShow: boolean;
  message: string;
  id: string;
}

export const EventSourceContext = createContext<EventSourceContextValue>({} as EventSourceContextValue);

export const EventSourceProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [onlineNotificationInfo, setOnlineNotificationInfo] = useState<OnlineNotificationData>({
    isShow: false,
    message: '',
    id: '-1',
  });
  const [isSSEOpen, setIsSSEOpen] = useState(false);

  const { notification } = useAntStaticComponent();

  useEffect(() => {
    let eventSource;
    try {
      eventSource = new EventSource('/fastApi/api/v1/message/sse');

      eventSource.addEventListener('open', event => {
        setIsSSEOpen(true);
      });
      eventSource.addEventListener(DynamicNotificationType.OnlineNotification, event => {
        setOnlineNotificationInfo({ isShow: true, message: event?.data ?? '', id: event?.lastEventId ?? '-1' });
      });
    } catch (err) {
      console.log('err', err);
    }
    return () => {
      eventSource?.close();
      setIsSSEOpen(false);
    };
  }, []);

  useEffect(() => {
    if (onlineNotificationInfo.isShow && onlineNotificationInfo.message) {
      const notificationMessage = _safeHTML({ htmlString: onlineNotificationInfo.message });
      if (notificationMessage) {
        notification.info({
          message: '通知',
          description: <div>{notificationMessage}</div>,
          icon: <MyIcon icon="alert-circle1" size={24} color="#7079fe" />,
          duration: null,
        });
      }
    }
  }, [isSSEOpen, onlineNotificationInfo.isShow, onlineNotificationInfo.message, onlineNotificationInfo.id]);

  const contextValue = {
    onlineNotificationInfo,
    isSSEOpen,
  };

  return <EventSourceContext.Provider value={contextValue}>{children}</EventSourceContext.Provider>;
};
